<!-- friends-list.html -->
<div class="min-h-[200px]">

  <!-- Loading (first page) -->
  <ng-container *ngIf="isLoading && items.length === 0">
    <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
      <div *ngFor="let i of [1,2,3,4,5,6,7,8]"
           class="animate-pulse bg-white rounded-2xl p-5 flex flex-col items-center gap-3 shadow-sm">
        <div class="w-20 h-20 rounded-full bg-gray-200"></div>
        <div class="w-28 h-4 rounded bg-gray-200"></div>
        <div class="w-20 h-3 rounded bg-gray-200"></div>
        <div class="w-24 h-8 rounded-full bg-gray-200"></div>
      </div>
    </div>
  </ng-container>

  <!-- Error -->
  <ng-container *ngIf="error && items.length === 0">
    <div class="flex flex-col items-center justify-center py-20 text-center gap-3">
      <p class="text-gray-400 text-sm">{{ error }}</p>
      <button type="button" (click)="loadNextPage()"
        class="rounded-xl bg-[#D31148] px-5 py-2 text-sm font-semibold text-white hover:bg-[#b80e3d] transition-colors">
        Try again
      </button>
    </div>
  </ng-container>

  <!-- Empty -->
  <ng-container *ngIf="!isLoading && !error && allLoaded && items.length === 0">
    <div class="flex flex-col items-center justify-center py-20 text-center">
      <p class="text-gray-400 text-sm">
        {{ isOwnProfile ? "You don't have any friends yet." : 'No friends to show yet.' }}
      </p>
    </div>
  </ng-container>

  <!-- List -->
  <ng-container *ngIf="items.length > 0">
    <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
      @for (item of items; track trackById($index, item)) {
        <div class="profile-card group bg-white rounded-2xl p-5 flex flex-col items-center gap-2 shadow-sm
                     transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-md">

          <a [routerLink]="['/profile', item.userName, 'post']" class="flex flex-col items-center gap-2 no-underline">
            <img
              [src]="item?.profileImage?.urlMedium ?? item?.profileImage?.urlSmall ?? avatarFallback(item.displayName)"
              [alt]="item.displayName"
              class="w-20 h-20 rounded-full object-cover ring-2 ring-transparent group-hover:ring-brand-400 transition-all duration-200" />

            <div class="text-center mt-1">
              <p class="font-semibold text-gray-900 text-sm leading-tight line-clamp-1 group-hover:text-brand-500 transition-colors duration-150">
                {{ item.displayName }}
              </p>
              <p class="text-gray-400 text-xs mt-0.5">&#64;{{ item.userName }}</p>
            </div>
          </a>

          <!-- Action button — reflects the *viewer's* relationship to this person -->
          <ng-container [ngSwitch]="isOwnProfile ? 'FRIENDS' : item.friendshipStatus">

            <button *ngSwitchCase="'NONE'" type="button" (click)="addFriend(item, $event)"
              [disabled]="actionPendingFor === item._id"
              class="mt-1 rounded-full border border-[#D31148] px-4 py-1.5 text-xs font-semibold text-[#D31148] hover:bg-rose-50 transition-colors disabled:opacity-50">
              Add Friend
            </button>

            <button *ngSwitchCase="'PENDING_SENT'" type="button" (click)="cancelRequest(item, $event)"
              [disabled]="actionPendingFor === item._id"
              class="mt-1 rounded-full border border-gray-300 px-4 py-1.5 text-xs font-semibold text-gray-500 hover:bg-gray-50 transition-colors disabled:opacity-50">
              Cancel Request
            </button>

            <div *ngSwitchCase="'PENDING_RECEIVED'" class="mt-1 flex items-center gap-2">
              <button type="button" (click)="acceptRequest(item, $event)"
                [disabled]="actionPendingFor === item._id"
                class="rounded-full bg-[#D31148] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#b80e3d] transition-colors disabled:opacity-50">
                Accept
              </button>
              <button type="button" (click)="declineRequest(item, $event)"
                [disabled]="actionPendingFor === item._id"
                class="rounded-full border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-500 hover:bg-gray-50 transition-colors disabled:opacity-50">
                Decline
              </button>
            </div>

            <button *ngSwitchCase="'FRIENDS'" type="button" (click)="promptUnfriend(item, $event)"
              [disabled]="actionPendingFor === item._id"
              class="mt-1 rounded-full bg-gray-100 px-4 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-200 transition-colors disabled:opacity-50">
              {{ isOwnProfile ? 'Unfriend' : 'Friends' }}
            </button>

          </ng-container>
        </div>
      }
    </div>

    <!-- Load more -->
    <div *ngIf="!allLoaded" class="flex justify-center py-6">
      <button type="button" (click)="loadNextPage()" [disabled]="isLoading"
        class="rounded-xl border border-[#e6e6e6] bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 hover:border-[#D31148] hover:text-[#D31148] transition-colors disabled:opacity-50">
        {{ isLoading ? 'Loading…' : 'Load more' }}
      </button>
    </div>
  </ng-container>

  <app-confirmation-popup [isOpen]="showLoginPrompt" icon="user-add" title="Login Required"
    [message]="'To ' + loginPromptAction" [highlightedName]="loginPromptUserName"
    messageSuffix="you need to be logged in first" confirmLabel="Log In" cancelLabel="Close"
    (confirmed)="goToLogin()" (cancelled)="closeLoginPrompt()">
  </app-confirmation-popup>

  <app-friend-request-limit-popup [isOpen]="showFriendLimitPopup" [hours]="friendLimitHours"
    (closed)="showFriendLimitPopup = false"></app-friend-request-limit-popup>

  <app-confirmation-popup [isOpen]="showUnfriendConfirm" icon="warning" title="Unfriend User"
    message="Are you sure you want to unfriend this user?" confirmLabel="Unfriend" cancelLabel="Cancel"
    (confirmed)="confirmUnfriend()" (cancelled)="cancelUnfriendConfirm()"></app-confirmation-popup>

</div>